/// FOURJS_START_COPYRIGHT(D,2026)
/// Property of Four Js*
/// (c) Copyright Four Js 2026, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT
"use strict";

modulum("FilterableListItemWidget", ["WidgetBase"], function (context, cls) {
  /**
   * Single item inside a FilterableListWidget.
   *
   * DOM structure: dragHandle + badge + label + controls (visibility icon).
   * The item manages its own visual state (visible, movable, hidable, badge)
   * and emits action events when controls are clicked.
   *
   * @class FilterableListItemWidget
   * @memberOf classes
   * @extends classes.WidgetBase
   */
  cls.FilterableListItemWidget = context.oo.Class(cls.WidgetBase, function ($super) {
    return /** @lends classes.FilterableListItemWidget.prototype */ {
      __name: "FilterableListItemWidget",

      /**
       * The item data object
       * @type {?FilterableListItemData}
       */
      _itemData: null,

      /**
       * DOM: drag handle element
       * @type {HTMLElement|null}
       */
      _dragHandleElement: null,

      /**
       * DOM: position badge input — readonly until the list puts it in edit mode
       * @type {HTMLInputElement|null}
       */
      _badgeElement: null,

      /**
       * DOM: label element
       * @type {HTMLElement|null}
       */
      _labelElement: null,

      /**
       * DOM: visually hidden state text for screen readers
       * @type {HTMLElement|null}
       */
      _srStateElement: null,

      /**
       * DOM: controls container element (carries the locked-state tooltip)
       * @type {HTMLElement|null}
       */
      _controlsElement: null,

      /**
       * DOM: visibility toggle button element
       * @type {HTMLElement|null}
       */
      _visibilityIconElement: null,

      /**
       * DOM: inner <i> icon inside the visibility button
       * @type {HTMLElement|null}
       */
      _visibilityIconInner: null,

      /**
       * Whether this item can be dragged to reorder
       * @type {boolean}
       */
      _movable: true,

      /**
       * Whether the user can toggle this item's visibility
       * @type {boolean}
       */
      _hidable: true,

      /**
       * Whether this item is visible
       * @type {boolean}
       */
      _visible: true,

      /**
       * Consumer-provided extra state text, appended to the screen reader state
       * @type {string}
       */
      _stateText: "",

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._dragHandleElement = this._element.getElementsByClassName("gbc_FilterableListItemWidget__dragHandle")[0];
        this._badgeElement = this._element.getElementsByClassName("gbc_FilterableListItemWidget__badge")[0];
        this._labelElement = this._element.getElementsByClassName("gbc_FilterableListItemWidget__label")[0];
        this._srStateElement = this._element.getElementsByClassName("gbc_FilterableListItemWidget__srState")[0];
        this._controlsElement = this._element.getElementsByClassName("gbc_FilterableListItemWidget__controls")[0];
        this._visibilityIconElement = this._element.getElementsByClassName("gbc_FilterableListItemWidget__visibilityIcon")[0];
        this._visibilityIconInner = this._visibilityIconElement.querySelector(".zmdi");
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        if (domEvent.target.closest(".gbc_FilterableListItemWidget__visibilityIcon")) {
          this._onVisibilityClick(domEvent);
        }
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * @inheritDoc
       */
      manageKeyDown: function (keyString, domKeyEvent, repeat) {
        // the row carries the roving tab stop: its keys are the list keyboard interaction
        const listWidget = this.getParentWidget();
        if (listWidget instanceof cls.FilterableListWidget && listWidget.manageItemKeyDown(this, keyString, domKeyEvent)) {
          return true;
        }
        return $super.manageKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._itemData = null;
        this._dragHandleElement = null;
        this._badgeElement = null;
        this._labelElement = null;
        this._srStateElement = null;
        this._controlsElement = null;
        this._visibilityIconElement = null;
        this._visibilityIconInner = null;
        $super.destroy.call(this);
      },

      // ── Data ────────────────────────────────────────────────────────────────

      /**
       * Set the item data and update all visual state accordingly.
       * @param {FilterableListItemData} itemData
       */
      setItemData: function (itemData) {
        this._itemData = itemData;
        if (this._labelElement) {
          this._labelElement.textContent = itemData.label;
        }
        if (this._badgeElement) {
          this._badgeElement.setAttribute("aria-label", gbc.I18NService.t("gwc.filterableList.positionInputLabel").replace("{name}", itemData.label));
        }
        this._updateVisibilityButtonLabel();
        if (itemData.group) {
          this._element.dataset.group = itemData.group;
        } else {
          delete this._element.dataset.group;
        }
        if (itemData.data) {
          this._stateText = itemData.data.stateText || "";
          this.setMovable(itemData.data.movable !== false);
          this.setHidable(itemData.data.hidable !== false);
          this.setItemVisible(itemData.data.visible !== false);
        }
      },

      /**
       * Returns the reorder group of the item — items only reorder within their own group
       * @returns {?string}
       */
      getGroup: function () {
        if (!this._itemData) {
          return null;
        }
        return this._itemData.group || null;
      },

      /**
       * Returns the item data
       * @returns {?FilterableListItemData}
       */
      getItemData: function () {
        if (!this._itemData) {
          return null;
        }
        this._itemData.data.visible = this._visible;
        this._itemData.data.movable = this._movable;
        this._itemData.data.hidable = this._hidable;
        return this._itemData;
      },

      /**
       * Set the displayed position of the badge input
       * @param {string} text
       */
      setBadgeText: function (text) {
        if (this._badgeElement) {
          this._badgeElement.value = text;
        }
      },

      /**
       * Returns the position badge input
       * @returns {HTMLInputElement|null}
       */
      getBadgeElement: function () {
        return this._badgeElement;
      },

      /**
       * Returns the visibility toggle button
       * @returns {HTMLButtonElement|null}
       */
      getVisibilityButtonElement: function () {
        return this._visibilityIconElement;
      },

      /**
       * Set visible state — updates the visibility icon (eye / eye-off)
       * @param {boolean} visible
       */
      setItemVisible: function (visible) {
        this._visible = visible;
        this.toggleClass("item-hidden", !visible);
        if (this._visibilityIconInner) {
          this._visibilityIconInner.classList.remove("zmdi-eye", "zmdi-eye-off");
          this._visibilityIconInner.classList.add(visible ? "zmdi-eye" : "zmdi-eye-off");
        }
        this._updateVisibilityButtonLabel();
        if (this._controlsElement) {
          if (this._hidable) {
            this._controlsElement.title = "";
          } else {
            this._controlsElement.title = visible
              ? gbc.I18NService.t("gwc.filterableList.cantHide")
              : gbc.I18NService.t("gwc.filterableList.cantShow");
          }
        }
        this._updateStateText();
      },

      /**
       * Name the visibility button with the action it performs (hide when visible, show when hidden)
       * @private
       */
      _updateVisibilityButtonLabel: function () {
        if (!this._visibilityIconElement || !this._itemData) {
          return;
        }
        const key = this._visible ? "gwc.filterableList.hideColumn" : "gwc.filterableList.showColumn";
        this._visibilityIconElement.setAttribute("aria-label", gbc.I18NService.t(key).replace("{name}", this._itemData.label));
      },

      /**
       * Returns whether the item is visible
       * @returns {boolean}
       */
      isItemVisible: function () {
        return this._visible;
      },

      /**
       * Set movable state — when false, locks the drag handle
       * @param {boolean} movable
       */
      setMovable: function (movable) {
        this._movable = movable;
        this.toggleClass("unmovable", !movable);
        if (this._dragHandleElement) {
          const icon = this._dragHandleElement.querySelector(".zmdi");
          if (icon) {
            icon.classList.remove("zmdi-drag-vertical", "zmdi-lock-outline");
            icon.classList.add(movable ? "zmdi-drag-vertical" : "zmdi-lock-outline");
          }
          this._dragHandleElement.classList.toggle("locked", !movable);
          this._dragHandleElement.title = movable ? "" : gbc.I18NService.t("gwc.filterableList.cantMove");
        }
        this._updateStateText();
      },

      /**
       * Returns whether the item is movable
       * @returns {boolean}
       */
      isMovable: function () {
        return this._movable;
      },

      /**
       * Set hidable state — when false, the visibility icon is grayed and non-interactive
       * @param {boolean} hidable
       */
      setHidable: function (hidable) {
        this._hidable = hidable;
        if (this._visibilityIconElement) {
          this._visibilityIconElement.classList.toggle("locked", !hidable);
          cls.DOMElementHelper.updateAttribute(this._visibilityIconElement, "aria-disabled", hidable ? null : "true");
        }
        if (this._controlsElement) {
          if (hidable) {
            this._controlsElement.title = "";
          } else {
            const key = this._visible ? "gwc.filterableList.cantHide" : "gwc.filterableList.cantShow";
            this._controlsElement.title = gbc.I18NService.t(key);
          }
        }
        this._updateStateText();
      },

      /**
       * Returns whether the item's visibility can be toggled by the user
       * @returns {boolean}
       */
      isHidable: function () {
        return this._hidable;
      },

      /**
       * Update the visually hidden state text — states must not be conveyed by icons alone
       * @private
       */
      _updateStateText: function () {
        if (!this._srStateElement) {
          return;
        }
        const states = [];
        if (!this._visible) {
          states.push(gbc.I18NService.t("gwc.filterableList.columnHidden"));
        }
        if (!this._movable) {
          states.push(gbc.I18NService.t("gwc.filterableList.columnUnmovable"));
        }
        if (!this._hidable) {
          states.push(gbc.I18NService.t(this._visible ? "gwc.filterableList.cantHide" : "gwc.filterableList.cantShow"));
        }
        if (this._stateText) {
          states.push(this._stateText);
        }
        this._srStateElement.textContent = states.join(", ");
      },

      /**
       * Toggle the item visibility, as clicking the eye does.
       * Ignored when the item is not hidable.
       */
      toggleVisibility: function () {
        if (!this._hidable || !this._itemData) {
          return;
        }
        this.emit(context.constants.widgetEvents.listItemToggleVisibility, { id: this._itemData.id });
      },

      /**
       * Handle click on the visibility icon
       * @param {MouseEvent} evt
       * @private
       */
      _onVisibilityClick: function (evt) {
        this.toggleVisibility();
      }
    };
  });
});
